import React, { useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { useSearchParams } from 'react-router-dom';
import { ROLES } from '../../utils/page-permissions';
import './Settings.css';
import CRMConfiguration from './CRMConfiguration';
import TeamManagement from './TeamManagement';
import SLASettings from './SLASettings';
import EmailTemplates from './EmailTemplates';
import StoreLists from './StoreLists';
import RecruitmentPipeline from './RecruitmentPipeline';
import JobTemplates from './JobTemplates';
import OnboardingChecklist from './OnboardingChecklist';
import InterviewScheduling from './InterviewScheduling';

// Import settings components (will be created later)
// import ProfileSettings from './ProfileSettings';
// import PeoplePlanSettings from './PeoplePlanSettings';
// import HRSettings from './HRSettings';
// import TASettings from './TASettings';
// import LDSettings from './LDSettings';

function Settings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get('tab') || 'hr';
  const [showCRMConfiguration, setShowCRMConfiguration] = useState(false);
  const [showTeamManagement, setShowTeamManagement] = useState(false);
  const [showSLASettings, setShowSLASettings] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [showStoreLists, setShowStoreLists] = useState(false);
  const [showRecruitmentPipeline, setShowRecruitmentPipeline] = useState(false);
  const [showJobTemplates, setShowJobTemplates] = useState(false);
  const [showOnboardingChecklist, setShowOnboardingChecklist] = useState(false);
  const [showInterviewScheduling, setShowInterviewScheduling] = useState(false);

  const tabs = [
    // Hidden for now - uncomment to re-enable
    // { id: 'profile', label: 'Profile', icon: '👤', allowed: Object.values(ROLES) },
    // { id: 'peopleplan', label: 'People Plan', icon: '📊', allowed: Object.values(ROLES) },
    { id: 'hr', label: 'HR', icon: '👥', allowed: [ROLES.HR, ROLES.ADMIN] },
    { id: 'ta', label: 'TA', icon: '🎯', allowed: [ROLES.TA, ROLES.ADMIN] },
    // { id: 'ld', label: 'L&D', icon: '📚', allowed: [ROLES.LD, ROLES.LD_DIRECTOR, ROLES.ADMIN] },
  ];

  const handleTabChange = (tabId) => {
    setSearchParams({ tab: tabId });
  };

  const renderTabContent = () => {
    switch (currentTab) {
      case 'profile':
        return <ProfilePlaceholder />;
      case 'peopleplan':
        return <PeoplePlanPlaceholder />;
      case 'hr':
        return <HRPlaceholder
          showCRMConfiguration={showCRMConfiguration}
          setShowCRMConfiguration={setShowCRMConfiguration}
          showTeamManagement={showTeamManagement}
          setShowTeamManagement={setShowTeamManagement}
          showSLASettings={showSLASettings}
          setShowSLASettings={setShowSLASettings}
          showEmailTemplates={showEmailTemplates}
          setShowEmailTemplates={setShowEmailTemplates}
          showStoreLists={showStoreLists}
          setShowStoreLists={setShowStoreLists}
        />;
      case 'ta':
        return <TAPlaceholder
          showRecruitmentPipeline={showRecruitmentPipeline}
          setShowRecruitmentPipeline={setShowRecruitmentPipeline}
          showJobTemplates={showJobTemplates}
          setShowJobTemplates={setShowJobTemplates}
          showOnboardingChecklist={showOnboardingChecklist}
          setShowOnboardingChecklist={setShowOnboardingChecklist}
          showInterviewScheduling={showInterviewScheduling}
          setShowInterviewScheduling={setShowInterviewScheduling}
        />;
      case 'ld':
        return <LDPlaceholder />;
      default:
        return <ProfilePlaceholder />;
    }
  };

  const token = localStorage.getItem('authToken') || '';
  const decodedToken = token ? jwtDecode(token) : {};

  const filteredTabs = tabs.filter((tab) => {
    return tab.allowed.includes(decodedToken?.role?.toLowerCase());
  })

  return (
    <div className="settings-page">
      <div className="settings-container">
        {/* Page Header */}
        <div className="settings-header">
          <h1 className="settings-title">⚙️ Settings</h1>
          <p className="settings-subtitle">Manage your application preferences and configurations</p>
        </div>

        {/* Tab Navigation */}
        <div className="settings-tabs">
          {filteredTabs.map((tab) => (
            <button
              key={tab.id}
              className={`settings-tab ${currentTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="settings-content">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}

// Placeholder components (will be replaced with actual components later)
function ProfilePlaceholder() {
  return (
    <div className="settings-placeholder">
      <div className="placeholder-icon">👤</div>
      <h2>Profile Settings</h2>
      <p>Manage your personal profile, preferences, and account settings.</p>
      <div className="placeholder-items">
        <div className="placeholder-item">
          <strong>Personal Information</strong>
          <span>Update your name, email, and contact details</span>
        </div>
        <div className="placeholder-item">
          <strong>Password & Security</strong>
          <span>Change your password and security preferences</span>
        </div>
        <div className="placeholder-item">
          <strong>Notifications</strong>
          <span>Configure your notification preferences</span>
        </div>
        <div className="placeholder-item">
          <strong>Appearance</strong>
          <span>Customize the look and feel of your interface</span>
        </div>
      </div>
    </div>
  );
}

function PeoplePlanPlaceholder() {
  return (
    <div className="settings-placeholder">
      <div className="placeholder-icon">📊</div>
      <h2>People Plan Settings</h2>
      <p>Configure settings related to workforce planning and employee management.</p>
      <div className="placeholder-items">
        <div className="placeholder-item">
          <strong>Workflow Templates</strong>
          <span>Manage templates for employee workflows</span>
        </div>
        <div className="placeholder-item">
          <strong>Position Categories</strong>
          <span>Configure position types and categories</span>
        </div>
        <div className="placeholder-item">
          <strong>Approval Workflows</strong>
          <span>Set up approval chains and processes</span>
        </div>
        <div className="placeholder-item">
          <strong>Custom Fields</strong>
          <span>Add custom fields to employee records</span>
        </div>
      </div>
    </div>
  );
}

function HRPlaceholder({
  showCRMConfiguration,
  setShowCRMConfiguration,
  showTeamManagement,
  setShowTeamManagement,
  showSLASettings,
  setShowSLASettings,
  showEmailTemplates,
  setShowEmailTemplates,
  showStoreLists,
  setShowStoreLists
}) {
  // Render specific component if any is active
  if (showCRMConfiguration) {
    return <CRMConfiguration onBack={() => setShowCRMConfiguration(false)} />;
  }
  if (showTeamManagement) {
    return <TeamManagement onBack={() => setShowTeamManagement(false)} />;
  }
  if (showSLASettings) {
    return <SLASettings onBack={() => setShowSLASettings(false)} />;
  }
  if (showEmailTemplates) {
    return <EmailTemplates onBack={() => setShowEmailTemplates(false)} />;
  }
  if (showStoreLists) {
    return <StoreLists onBack={() => setShowStoreLists(false)} />;
  }

  // Default: show tiles
  return (
    <div className="settings-placeholder">
      <div className="placeholder-icon">👥</div>
      <h2>HR Settings</h2>
      <p>Configure HR-specific settings including CRM, ticketing, and team management.</p>
      <div className="placeholder-items">
        <div
          className="placeholder-item clickable"
          onClick={() => setShowCRMConfiguration(true)}
        >
          <strong>CRM Configuration</strong>
          <span>Manage ticket categories, subcategories, and assignment rules</span>
        </div>
        {/* <div
          className="placeholder-item clickable"
          onClick={() => setShowTeamManagement(true)}
        >
          <strong>Team Management</strong>
          <span>Configure HR teams and member assignments</span>
        </div> */}
        {/* <div
          className="placeholder-item clickable"
          onClick={() => setShowSLASettings(true)}
        >
          <strong>SLA Settings</strong>
          <span>Set up SLA thresholds and notification rules</span>
        </div>
        <div
          className="placeholder-item clickable"
          onClick={() => setShowEmailTemplates(true)}
        >
          <strong>Email Templates</strong>
          <span>Customize email templates for notifications</span>
        </div> */}
        <div
          className="placeholder-item clickable"
          onClick={() => setShowStoreLists(true)}
        >
          <strong>Store Lists</strong>
          <span>Manage store locations and hierarchy</span>
        </div>
      </div>
    </div>
  );
}

function TAPlaceholder({
  showRecruitmentPipeline,
  setShowRecruitmentPipeline,
  showJobTemplates,
  setShowJobTemplates,
  showOnboardingChecklist,
  setShowOnboardingChecklist,
  showInterviewScheduling,
  setShowInterviewScheduling
}) {
  // Render specific component if any is active
  if (showRecruitmentPipeline) {
    return <RecruitmentPipeline onBack={() => setShowRecruitmentPipeline(false)} />;
  }
  if (showJobTemplates) {
    return <JobTemplates onBack={() => setShowJobTemplates(false)} />;
  }
  if (showOnboardingChecklist) {
    return <OnboardingChecklist onBack={() => setShowOnboardingChecklist(false)} />;
  }
  if (showInterviewScheduling) {
    return <InterviewScheduling onBack={() => setShowInterviewScheduling(false)} />;
  }

  // Default: show tiles
  return (
    <div className="settings-placeholder">
      <div className="placeholder-icon">🎯</div>
      <h2>TA Settings</h2>
      <p>Configure Talent Acquisition settings for recruiting and onboarding.</p>
      <div className="placeholder-items">
        <div
          className="placeholder-item clickable"
          onClick={() => setShowRecruitmentPipeline(true)}
        >
          <strong>Recruitment Pipeline</strong>
          <span>Configure recruitment stages and workflows</span>
        </div>
        {/* Hidden for now - uncomment to re-enable
        <div
          className="placeholder-item clickable disabled"
          onClick={() => setShowJobTemplates(true)}
        >
          <strong>Job Templates</strong>
          <span>Manage job posting templates and requirements</span>
        </div>
        <div
          className="placeholder-item clickable disabled"
          onClick={() => setShowOnboardingChecklist(true)}
        >
          <strong>Onboarding Checklist</strong>
          <span>Set up onboarding tasks and checklists</span>
        </div>
        <div
          className="placeholder-item clickable disabled"
          onClick={() => setShowInterviewScheduling(true)}
        >
          <strong>Interview Scheduling</strong>
          <span>Configure interview types and scheduling rules</span>
        </div>
        */}
      </div>
    </div>
  );
}

function LDPlaceholder() {
  return (
    <div className="settings-placeholder">
      <div className="placeholder-icon">📚</div>
      <h2>L&D Settings</h2>
      <p>Configure Learning & Development settings for training and performance management.</p>
      <div className="placeholder-items">
        <div className="placeholder-item disabled">
          <strong>Training Programs</strong>
          <span>Manage training courses and curricula</span>
        </div>
        <div className="placeholder-item disabled">
          <strong>Performance Metrics</strong>
          <span>Configure KPIs and performance indicators</span>
        </div>
        <div className="placeholder-item disabled">
          <strong>Goal Templates</strong>
          <span>Set up goal templates and OKR frameworks</span>
        </div>
        <div className="placeholder-item disabled">
          <strong>Review Cycles</strong>
          <span>Configure performance review schedules</span>
        </div>
        <div className="placeholder-item disabled">
          <strong>Certification Tracking</strong>
          <span>Manage certifications and compliance training</span>
        </div>
      </div>
    </div>
  );
}

export default Settings;
